import React, { useState, useRef } from 'react';
import { object, bool, string, func, array } from 'prop-types';
import { Button, ButtonGroup, Checkbox, Input, TextArea } from '../../../../ps-aspect-design';
import { Alert, Modal, Tooltip } from 'ps-design';
import cn from 'classnames';
import Dropdown from 'ps-components/Dropdown/Dropdown';
import InPlaceIndicator from 'ps-components/LoadingIndicator/InPlaceIndicator/InPlaceIndicator';
import useOutsideAlerter from 'ps-components/Utils/useOutsideAlerter';

import AddCollectionDropdown from './AddCollectionDropdown';
import './ReportBulkActions.scss';

ReportBulkActions.propTypes = {
  addReportsToCollections: func,
  createCollection: func,
  deleteAllSelectedReports: func,
  deselectAllReports: func,
  deselectPopular: func,
  popularFilter: bool,
  reportDeleteError: string,
  reports: object,
  selectedPopularReports: array,
  setViewPreference: func,
  viewType: string,
  removeReportsFromCollections: func,
  refresh: func,
  userSiteAdmin: bool,
  userGroupAdmin: bool
};

export default function ReportBulkActions(props) {
  const [showAddCollectionDropdown, setShowAddCollectionDropdown] = useState();
  const [newCollectionName, setNewCollectionName] = useState('');
  const [newCollectionDescription, setNewCollectionDescription] = useState('');
  const [createCollectionLoading, setCreateCollectionLoading] = useState(false);

  let deleteModalRef = useRef();
  let removeModalRef = useRef();
  let createCollectionModalRef = useRef();
  let newCollectionNameRef = useRef();
  const wrapperRef = useRef();
  useOutsideAlerter(wrapperRef, setShowAddCollectionDropdown);

  const reportsSelected = props.popularFilter ? props.selectedPopularReports : getReportsSelected();
  const reportsSelectedCount = reportsSelected?.length || 0;
  const bulkActionsClass = cn({
    'bulk-actions': true,
    'bulk-actions--visible': reportsSelected?.length
  });

  const standardSelected = reportsSelected?.some(r => r?.scope?.code === 'global') || false;
  const personalSelected = reportsSelected?.some(r => r?.scope?.code === 'personal') || false;
  const canAddReportsToCollections = props.userSiteAdmin || props.userGroupAdmin;

  function deleteReports() {
    if (props.deleteAllSelectedReports && props.reports) {
      deleteModalRef.hide();
      props.deleteAllSelectedReports(props.reports);
    }
  }

  function removeReports() {
    if (props.removeReportsFromCollections && reportsSelectedCount > 0) {
      removeModalRef?.hide();
      props.removeReportsFromCollections({ collections: reportsSelected }).then(status => {
        if (status === 200) {
          props.refresh();
        }
      });
    }
  }

  function getReportsSelected() {
    if (props.reports && props.reports.edges) {
      return props.reports.edges.filter(report => report.selectedForEdit);
    } else {
      return null;
    }
  }

  function toggleView(viewPreference) {
    props.setViewPreference(viewPreference);
  }

  function createNewCollection() {
    setShowAddCollectionDropdown(false);
    createCollectionModalRef?.show();
    newCollectionNameRef?.current?.focus();
  }

  function createCollection(createCollectionModalRef) {
    setCreateCollectionLoading(true);

    props
      .createCollection({
        name: newCollectionName,
        description: newCollectionDescription,
        reportIds: reportsSelected.map(r => Number(r.id))
      })
      .then(collection => {
        if (collection !== 409) {
          setNewCollectionName('');
          setNewCollectionDescription('');
          createCollectionModalRef?.hide();
          props.deselectAllReports();
        }

        setCreateCollectionLoading(false);
      });
  }

  // TODO only show add report to collection link for group and site admins
  return (
    <div className={bulkActionsClass}>
      {props.reportDeleteError && (
        <Alert className="pxl-margin-bottom-large" message={props.reportDeleteError} close={true} />
      )}
      <Checkbox
        checked={true}
        onChange={() => {
          if (props.popularFilter) {
            props.deselectPopular();
          } else {
            props.deselectAllReports();
          }
        }}
        className="bulk-actions__deselect"
      />
      <div className="bulk-actions__selectedtext">{reportsSelectedCount} Selected</div>
      <div className="divider" />

      {props.popularFilter ? (
        <div className="bulk-actions__delete" onClick={() => removeModalRef.show()}>
          <i className="icon-trash" />
          Remove Reports From Collections
        </div>
      ) : (
        <>
          <div
            className={cn('bulk-actions__delete', { disabled: standardSelected })}
            onClick={() => {
              if (!standardSelected) {
                deleteModalRef.show();
              }
            }}
          >
            <i className={cn('icon-trash', { disabled: standardSelected })} />
            Delete
            {standardSelected && (
              <Tooltip
                className="standard-selected-icon"
                hoverText="Cannot delete standard reports"
                id="standard-selected-icon"
                position="bottom"
              />
            )}
          </div>

          {canAddReportsToCollections && (
            <div
              className={cn('bulk-actions__add-collection', { disabled: personalSelected })}
              data-test-id="bulk-actions__add-collection"
            >
              <i
                className="icon-plus"
                onClick={() => {
                  if (!personalSelected) {
                    setShowAddCollectionDropdown(true);
                  }
                }}
              />
              <Dropdown
                dropdownClass="add-collection__dropdown"
                text={`Add selected ${reportsSelectedCount > 1 ? 'reports' : 'report'} to`}
                disableBlur={true}
                disabled={personalSelected}
                customRenderComponent={AddCollectionDropdown}
                customRenderProps={{
                  collections: [],
                  render: showAddCollectionDropdown,
                  wrapperRef,
                  createNewCollection,
                  addReportsToCollections: props.addReportsToCollections,
                  reportsSelected,
                  setShowAddCollectionDropdown,
                  userSiteAdmin: props.userSiteAdmin,
                  userGroupAdmin: props.userGroupAdmin,
                  deselectAllReports: props.deselectAllReports
                }}
                parentTriggerFunc={() => setShowAddCollectionDropdown(true)}
              />
              {personalSelected && (
                <Tooltip
                  className="standard-selected-icon"
                  hoverText="Cannot add personal reports to collections"
                  id="personal-selected-icon"
                  position="bottom"
                />
              )}
            </div>
          )}
        </>
      )}

      {!props.popularFilter && (
        <div className="bulk-actions__view">
          <button
            className={`bulk-actions__cards${props.viewType === 'card' ? '-active' : ''}`}
            onClick={() => toggleView('card')}
            aria-label="View card layout for reports"
          >
            <span className="icon-layout" />
          </button>
          <button
            className={`bulk-actions__table${props.viewType === 'list' ? '-active' : ''}`}
            onClick={() => toggleView('list')}
            aria-label="View table layout for reports"
          >
            <span className="icon-menu" />
          </button>
        </div>
      )}

      <Modal
        ref={deleteModal => {
          deleteModalRef = deleteModal;
        }}
        id="deleteModal"
        className="modal__delete"
        displayCloseButton={true}
      >
        <div className="modal__delete-contents">
          <div className="modal__delete-title">
            Are you sure you want to delete {reportsSelectedCount} {reportsSelectedCount > 1 ? 'reports' : 'report'}{' '}
            from report library?
          </div>
          <div className="modal__delete-actions">
            <ButtonGroup>
              <Button variant="secondary" onClick={() => deleteModalRef.hide()}>
                No
              </Button>
              <Button variant="primary" onClick={deleteReports}>
                Yes
              </Button>
            </ButtonGroup>
          </div>
        </div>
      </Modal>

      <Modal
        ref={removeModal => {
          removeModalRef = removeModal;
        }}
        id="removeModal"
        className="modal__delete"
        displayCloseButton={true}
      >
        <div className="modal__delete-contents">
          <div className="modal__delete-title">
            Are you sure you want to remove {reportsSelectedCount} {reportsSelectedCount > 1 ? 'reports ' : 'report '}
            from {reportsSelectedCount > 1 ? 'their collections' : 'its collection'}?
          </div>
          <div className="modal__delete-actions">
            <ButtonGroup>
              <Button variant="secondary" onClick={() => removeModalRef.hide()}>
                No
              </Button>
              <Button variant="primary" onClick={removeReports}>
                Yes
              </Button>
            </ButtonGroup>
          </div>
        </div>
      </Modal>

      <Modal
        ref={createCollectionModal => {
          createCollectionModalRef = createCollectionModal;
        }}
        id="createCollectionModal"
        className="modal__create-collection"
        displayCloseButton={true}
      >
        <div className="modal__create-collection-contents">
          <div className="modal__create-collection-title">Create a New Popular Report Collection</div>
          <div className="modal__create-collection-description">
            Selected reports will be added to this new custom collection.
            <br />
            Access to each report in a collection is defined by the “Publish to” field on the Parameters tab within the
            report.
          </div>
          <div className="collection-inputs">
            <div className="input-title">Collection Name</div>
            <Input
              className="collection-input"
              value={newCollectionName}
              onChange={(event, value) => {
                setNewCollectionName(value);
              }}
              type="text"
              autoFocus
              ref={newCollectionNameRef}
              id="collection-name-input"
            />

            <div className="input-title">Collection Description (optional)</div>
            <TextArea
              className="collection-textarea"
              value={newCollectionDescription}
              onChange={(event, value) => {
                setNewCollectionDescription(value);
              }}
              type="text"
            />
          </div>
          <div className="modal__create-collection-actions">
            <ButtonGroup>
              {createCollectionLoading ? (
                <InPlaceIndicator />
              ) : (
                <>
                  <Button variant="secondary" onClick={() => createCollectionModalRef.hide()}>
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    disabled={!newCollectionName}
                    onClick={() => createCollection(createCollectionModalRef)}
                    data-testid="create-collection-submit__btn"
                  >
                    Create New Collection
                  </Button>
                </>
              )}
            </ButtonGroup>
          </div>
        </div>
      </Modal>
    </div>
  );
}
